import type { AnalyticsElementId } from "@/lib/analytics/analyticsElementId";

/**
 * Canonical FAQ copy for the audience pages.
 * Answers keep tracked links as segments, including their surrounding spaces.
 * The homepage uses landingFaqContent.ts for both its visible FAQ and structured data.
 */
type FaqAnswerLink = {
  text: string;
  href: string;
  /**
   * Each placement passes its own id — the links share a destination but not a control, and
   * only distinct ids say which answer sent a reader to the article. Totals across them stay
   * recoverable by grouping on the ids; the per-answer split would not be.
   */
  analyticsElementId: AnalyticsElementId;
};

export type FaqAnswerSegment = string | FaqAnswerLink;

type FaqItem = {
  question: string;
  answer: readonly FaqAnswerSegment[];
  defaultOpen?: boolean;
};

const ANTIBOTTING_ARTICLE_HREF = "/article/antibotting";
const BRAND_SAFETY_ARTICLE_HREF = "/article/brand-safety";

/**
 * Deep links from an answer to the anti-botting explainer sit on the phrases that name the
 * fraud-detection system itself; "Apply for verification" (partner onboarding) is a different
 * sense of the word and deliberately stays unlinked.
 */
export const faqItems: readonly FaqItem[] = [
  {
    question: "How does Evangelist work?",
    answer: [
      "Evangelist is a programmatic exchange for short-form creator inventory. You brief once, and we route approved assets to verified clipping networks across Shorts, TikTok, Reels & X, run ",
      {
        text: "anti-fraud verification",
        href: ANTIBOTTING_ARTICLE_HREF,
        analyticsElementId: "landing-faq:how-it-works-antibotting-link",
      },
      " with zkTLS-backed data, and handle brand safety and creator payments.",
    ],
    defaultOpen: false,
  },
  {
    question: "What problem does Evangelist solve?",
    answer: [
      "It removes fragmented sourcing and inconsistent metrics. You get a single, enterprise channel for quality supply, standardized reporting, and billing only on ",
      {
        text: "verified views",
        href: ANTIBOTTING_ARTICLE_HREF,
        analyticsElementId: "landing-faq:problem-antibotting-link",
      },
      ".",
    ],
    defaultOpen: true,
  },
  {
    question: "How does Evangelist ensure brand safety and quality?",
    answer: [
      "Every clip passes ",
      {
        text: "brand-safety checks",
        href: BRAND_SAFETY_ARTICLE_HREF,
        analyticsElementId: "landing-faq:brand-safety-article-link",
      },
      " built on the IAB Tech Lab Content Taxonomy's brand-safety floor and suitability tiers. Its categories and risk levels give us consistent labels to detect, tag and score video content. In practice, the taxonomy is our vocabulary, not our policy: each campaign sets its own tolerances.",
    ],
  },
  {
    question: "How does Evangelist complement existing clipping platforms?",
    answer: [
      "We provide independent measurement, ",
      {
        text: "verification",
        href: ANTIBOTTING_ARTICLE_HREF,
        analyticsElementId: "landing-faq:complement-antibotting-link",
      },
      ", and settlement for branded short-form video content. Evangelist standardizes reporting and safety criteria across platforms, reduces fraud and operational overhead, and helps your existing clipping platforms scale faster.",
    ],
  },
  {
    question: "How can a brand or agency start buying?",
    answer: [
      {
        text: "Launch a campaign",
        href: "/signin",
        analyticsElementId: "landing-faq:launch-campaign-link",
      },
      " by signing in with your company Google or Microsoft account. If you’d like help planning your campaign, email sales@evangelist.so. Reporting and invoicing are standardized.",
    ],
  },
  {
    question: "Who supplies the inventory?",
    answer: [
      "Verified clipping platforms and creator networks supply the inventory. They publish compliant edits using your approved assets while Evangelist manages brand safety, ",
      {
        text: "verification",
        href: ANTIBOTTING_ARTICLE_HREF,
        analyticsElementId: "landing-faq:inventory-antibotting-link",
      },
      ", and payments.",
    ],
  },
  {
    question: "How do clipping platforms participate?",
    answer: [
      "Apply for verification. Approved partners access enterprise demand and standardized reporting, and Evangelist handles safety checks, measurement, and settlement so you can focus on creators and delivery.",
    ],
  },
];
